import { PinIcon, ImageIcon } from '../../icons/icons';
import { serverUrl } from '../../../config';

require('./index.css').toString();

export default class DynamicMap {

  static get toolbox() {
    return {
      icon: PinIcon,
      title: 'Dynamic Map',
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  static _normalizeStars(val) {
    if (Array.isArray(val)) return val.map(String);
    if (val && typeof val === 'object' && val.value !== undefined && val.value !== '') return [String(val.value)];
    if (val !== undefined && val !== null && val !== '') return [String(val)];
    return [];
  }

  constructor({ data, config, api, readOnly, block }) {
    this.api = api;
    this.readOnly = readOnly;
    this.block = block;
    this.config = config;
    this._data = {
      title: data.title || config?.title || '',
      zoom: data.zoom ?? config?.defaultZoom ?? 3,
      centerLat: data.centerLat ?? config?.defaultCenterLat ?? 24.201,
      centerLng: data.centerLng ?? config?.defaultCenterLng ?? 63.72,
      mapStyle: data.mapStyle || config?.mapStyle || 'light',
      region: data.region || '',
      country: data.country || '',
      city: data.city || '',
      brand: data.brand || '',
      service: data.service || '',
      industry: data.industry || '',
      star: DynamicMap._normalizeStars(data.star),
      guestRoom: data.guestRoom || '',
      meetingRoom: data.meetingRoom || '',
      capacity: data.capacity || '',
      popup: data.popup || 'leisure',
      url: data.url || '',
      mid: data.mid || '',
      x: data.x || '',
      y: data.y || '',
      width: data.width || '',
      height: data.height || '',
      maxWidth: data.maxWidth || '',
      maxHeight: data.maxHeight || '',
    };

    this._CSS = {
      dmModalApplyBtn: "dm-apply-btn",
      dmModalCancelBtn: "dm-cancel-btn",
      dmModalResetContainer: "dm-reset-container",
      dmModalResetBtn: "dm-reset-btn",
      dmOnscreenResetBtn: "dm-onscreen-reset-btn",
      mapStatus: "dynamic-map-status",
      mapRender: "dynamic-map-render",
      mapContainer: "dynamic-map-container",
      mapModalBtn: "dynamic-map-modal-show-btn",
      // standard css
      blockBtn: "block-btn",
      blockBtnSmall: "small",
      blockBtnLarge: "large",
      blockBtnLargeWide: "wide",
      largeIcon: "large-icon",
      xlIcon: "xl-icon",
      xxlIcon: "xxl-icon",
      save: "save",
      clear: "clear",
      cancel: "cancel",
      focusVisibleBtn: "focus-visible-btn",
      tuneWrapper: "editor-block-tune",
      tuneTitle: "editor-block-tune-title",
      tuneText: "editor-block-tune-text",
      tuneInput: "editor-block-tune-input",
      tuneButton: "editor-block-tune-button",
      editorFont: "editor-font",
      editorBlockToolbar: "editor-block-toolbar",
      editorBlockModalBackdrop: "editor-block-modal-backdrop",
      editorBlockModal: "editor-block-modal",
      editorBlockModalTitle: "editor-block-modal-title",
      editorBlockModalLabel: "editor-block-modal-label",
      editorBlockModalText: "editor-block-modal-text",
      editorBlockModalInput: "editor-block-modal-input",
      adminFont: "admin-font",
    }
    
    this._allHotels = [];
    this._iframe = null;
    this._savedMapState = null;
    this._brandLogos = config?.brandLogos || {};
    this._slideId = config?.slideId || '';

    // Bind the message handler so we can add/remove it
    this._onMessage = this._handleMessage.bind(this);

    this._onDocClick = (e) => {
      if (this._starDropdown && !this._starDropdown.contains(e.target)) this._closeStarMenu();
    };
    
  }

  render() {
    const css = this._CSS;
    this._container = document.createElement('div');
    this._container.classList.add(css.mapContainer);
    
    this._onScreenResetBtn = document.createElement('button');
    this._onScreenResetBtn.classList.add(css.dmOnscreenResetBtn, css.blockBtn, css.blockBtnLarge, css.blockBtnLargeWide, css.clear);
    this._onScreenResetBtn.textContent = 'Reset configuration';
    this._onScreenResetBtn.style.display = 'none';
    this._onScreenResetBtn.addEventListener('click', () => {
      this._onReset();
    });

    // hidden input to prevent empty block creation
    const hiddenInput = document.createElement('input');
    hiddenInput.setAttribute('style', 'visibility:hidden;width:0;height:0;padding:0;margin:0;border:0;position:absolute');
    this._container.appendChild(hiddenInput);

    // Map wrapper (iframe goes here)
    this._mapWrapper = document.createElement('div');
    this._mapWrapper.classList.add(css.mapRender);
    this._container.appendChild(this._mapWrapper);

    // Status indicator
    this._status = document.createElement('div');
    this._status.classList.add(css.mapStatus);
    this._container.appendChild(this._status);

    // Settings button (edit mode only)
    if (!this.readOnly) {
      this._settingsBtn = document.createElement('button');
      this._settingsBtn.classList.add(css.blockBtn, css.blockBtnLarge, css.blockBtnLargeWide, css.mapModalBtn);
      this._settingsBtn.innerText = 'Configure';
      this._settingsBtn.addEventListener('click', () => this._showModal());
      this._container.appendChild(this._onScreenResetBtn);
      this._container.appendChild(this._settingsBtn);

      this._modal = this._buildModal();
      this._container.appendChild(this._modal);
      this._updateResetButton();
    }

    // Listen for postMessage from the iframe
    window.addEventListener('message', this._onMessage);

    // Render the iframe
    this._renderIframe();

    return this._container;
  }

  _buildIframeSrc() {
    const params = new URLSearchParams({
      serverUrl: serverUrl,
      lat: this._data.centerLat,
      lng: this._data.centerLng,
      zoom: this._data.zoom,
      mapStyle: this._data.mapStyle,
      title: this._data.title,
      slideId: this._slideId,
      region: this._data.region,
      country: this._data.country,
      city: this._data.city,
      brand: this._data.brand?.value || '',
      service: this._data.service?.value || '',
      industry: this._data.industry || '',
      star: this._data.star.join(','),
      guestRoom: this._data.guestRoom || 0,
      meetingRoom: this._data.meetingRoom || 0,
      capacity: this._data.capacity || 0,
      popup: this._data.popup || 'leisure',
    });
    return `/dynamic-map.html?${params.toString()}`;
  }

  _renderIframe() {
    this._mapWrapper.innerHTML = '';

    const iframe = document.createElement('iframe');
    iframe.src = this._buildIframeSrc();
    this._iframe = iframe;
    this._mapWrapper.appendChild(iframe);
  }

  _postToIframe(type, payload) {
    if (this._iframe && this._iframe.contentWindow) {
      this._iframe.contentWindow.postMessage({ type, payload }, '*');
    }
  }

  _sendBrandLogos() {
    if (!this._brandLogos || !Object.keys(this._brandLogos).length) return;
    this._postToIframe('dynamic-map-theme-data', { brandLogos: this._brandLogos });
  }

  _handleMessage(event) {
    if (!event.data || typeof event.data !== 'object') return;
    if (event.data.slideId !== this._slideId) return;

    switch (event.data.type) {
      case 'dynamic-map-ready':
        this._setStatus('Map ready');
        setTimeout(() => this._hideStatus(), 2000);
        this._sendBrandLogos();
        break;

      case 'dynamic-map-hotels-loaded':
        this._allHotels = event.data.payload.hotels || [];
        this._allFilters = event.data.payload.filters
        this._setStatus(`${event.data.payload.count} hotels loaded`);
        setTimeout(() => this._hideStatus(), 3000);

        // Save initial map state for reset
        this._savedMapState = {
          zoom: this._data.zoom,
          centerLat: this._data.centerLat,
          centerLng: this._data.centerLng,
        };

        // Populate filter dropdowns from hotel data
        this._populateFilters();

        // Apply any pre-existing filter. Restoring on load must not reframe
        // the map, otherwise the saved zoom/center would be overwritten.
        if (this._data.region || this._data.country || this._data.city) {
          this._applyFilter(false);
        }
        break;

      case 'dynamic-map-zoom-changed':
        this._data.zoom = event.data.payload.zoom;
        this._data.centerLat = event.data.payload.centerLat;
        this._data.centerLng = event.data.payload.centerLng;
        if (this.block?.dispatchChange) {
          this.block.dispatchChange();
        }
        break;

      case 'dynamic-map-error':
        this._setStatus(event.data.payload.message || 'Map error');
        break;

      default:
        break;
    }
  }


  static REGION_MAP = {
    // Europe
    AL:'Europe',AD:'Europe',AT:'Europe',BY:'Europe',BE:'Europe',BA:'Europe',BG:'Europe',HR:'Europe',CY:'Europe',CZ:'Europe',
    DK:'Europe',EE:'Europe',FI:'Europe',FR:'Europe',DE:'Europe',GR:'Europe',HU:'Europe',IS:'Europe',IE:'Europe',IT:'Europe',
    XK:'Europe',LV:'Europe',LI:'Europe',LT:'Europe',LU:'Europe',MT:'Europe',MD:'Europe',MC:'Europe',ME:'Europe',NL:'Europe',
    MK:'Europe',NO:'Europe',PL:'Europe',PT:'Europe',RO:'Europe',RU:'Europe',SM:'Europe',RS:'Europe',SK:'Europe',SI:'Europe',
    ES:'Europe',SE:'Europe',CH:'Europe',UA:'Europe',GB:'Europe',VA:'Europe',GE:'Europe',AM:'Europe',AZ:'Europe',TR:'Europe',
    // Africa
    DZ:'Africa',AO:'Africa',BJ:'Africa',BW:'Africa',BF:'Africa',BI:'Africa',CV:'Africa',CM:'Africa',CF:'Africa',TD:'Africa',
    KM:'Africa',CD:'Africa',CG:'Africa',CI:'Africa',DJ:'Africa',EG:'Africa',GQ:'Africa',ER:'Africa',SZ:'Africa',ET:'Africa',
    GA:'Africa',GM:'Africa',GH:'Africa',GN:'Africa',GW:'Africa',KE:'Africa',LS:'Africa',LR:'Africa',LY:'Africa',MG:'Africa',
    MW:'Africa',ML:'Africa',MR:'Africa',MU:'Africa',MA:'Africa',MZ:'Africa',NA:'Africa',NE:'Africa',NG:'Africa',RW:'Africa',
    ST:'Africa',SN:'Africa',SC:'Africa',SL:'Africa',SO:'Africa',ZA:'Africa',SS:'Africa',SD:'Africa',TZ:'Africa',TG:'Africa',
    TN:'Africa',UG:'Africa',ZM:'Africa',ZW:'Africa',RE:'Africa',
    // Asia
    AF:'Asia',BH:'Asia',BD:'Asia',BT:'Asia',BN:'Asia',KH:'Asia',CN:'Asia',IN:'Asia',ID:'Asia',IR:'Asia',IQ:'Asia',IL:'Asia',
    JP:'Asia',JO:'Asia',KZ:'Asia',KW:'Asia',KG:'Asia',LA:'Asia',LB:'Asia',MY:'Asia',MV:'Asia',MN:'Asia',MM:'Asia',NP:'Asia',
    KP:'Asia',OM:'Asia',PK:'Asia',PS:'Asia',PH:'Asia',QA:'Asia',SA:'Asia',SG:'Asia',KR:'Asia',LK:'Asia',SY:'Asia',TW:'Asia',
    TJ:'Asia',TH:'Asia',TL:'Asia',TM:'Asia',AE:'Asia',UZ:'Asia',VN:'Asia',YE:'Asia',
    // Oceania
    AU:'Oceania',FJ:'Oceania',NZ:'Oceania',PG:'Oceania',WS:'Oceania',SB:'Oceania',TO:'Oceania',VU:'Oceania',
    // Americas
    AR:'Americas',BS:'Americas',BB:'Americas',BZ:'Americas',BO:'Americas',BR:'Americas',CA:'Americas',CL:'Americas',
    CO:'Americas',CR:'Americas',CU:'Americas',DO:'Americas',EC:'Americas',SV:'Americas',GT:'Americas',GY:'Americas',
    HT:'Americas',HN:'Americas',JM:'Americas',MX:'Americas',NI:'Americas',PA:'Americas',PY:'Americas',PE:'Americas',
    PR:'Americas',SR:'Americas',TT:'Americas',US:'Americas',UY:'Americas',VE:'Americas',AG:'Americas',DM:'Americas',
    GD:'Americas',KN:'Americas',LC:'Americas',VC:'Americas',AW:'Americas',CW:'Americas',
  };

  _getRegionForHotel(hotel) {
    const code = (hotel.country_code || '').toUpperCase();
    return DynamicMap.REGION_MAP[code] || 'Other';
  }

  _populateFilters() {
    if (!this._regionSelect) return;

    const { regions, brands, services, industries, stars } = this._allFilters;

    this._regionSelect.innerHTML = '<option value="">All Regions</option>';
    [...regions].sort().forEach(r => {
      const opt = document.createElement('option');
      opt.value = r;
      opt.textContent = r;
      this._regionSelect.appendChild(opt);
    });

    // Restore saved values and cascade
    if (this._data.region) {
      this._regionSelect.value = this._data.region;
      this._populateCountries();
      this._showRow(this._countryRow);

      if (this._data.country) {
        this._countrySelect.value = this._data.country;
        this._populateCities();
        this._showRow(this._cityRow);

        if (this._data.city) {
          this._citySelect.value = this._data.city;
        }
      }
    }

    if(brands.length > 0){
      this._brandSelect.innerHTML = '<option value="">Any</option>';
      brands.forEach((brand) => {
        const opt = document.createElement('option');
        opt.value = brand.code;
        opt.textContent = brand.name;
        this._brandSelect.appendChild(opt);
      });
    }
    
    if(this._data.brand){
      this._brandSelect.value = this._data.brand?.value || '';
    }

    if(services.length > 0){
      this._serviceSelect.innerHTML = '<option value="">Any</option>';
      services.forEach((service) => {
        const opt = document.createElement('option');
        opt.value = service.key;
        opt.textContent = service.name;
        this._serviceSelect.appendChild(opt);
      });
    }
    
    if(this._data.service){
      this._serviceSelect.value = this._data.service?.value || '';
    }

    if(industries.length > 0){
      this._industrySelect.innerHTML = '<option value="">Any</option>';
      industries.forEach((industry) => {
        const opt = document.createElement('option');
        opt.value = industry.key;
        opt.textContent = industry.name;
        this._industrySelect.appendChild(opt);
      });
    }
    
    if(this._data.industry){
      this._industrySelect.value = this._data.industry?.value || '';
    }

    if (stars.max && stars.min) {
      this._renderStarOptions(stars.min, stars.max);
    }
    
    // if(this._data.star){
    //   this._starSelect.value = this._data.star?.value || '';
    // }
  }

  _getFilteredByRegion() {
    if (!this._data.region) return this._allHotels;
    return this._allHotels.filter(h => this._getRegionForHotel(h) === this._data.region);
  }

  _populateCountries() {
    const { countries } = this._allFilters;
    const _countries = this._data.region === 'All Regions'? countries : countries.filter((country) => country.region === this._data.region);
    
    this._countrySelect.innerHTML = '<option value="">All Countries</option>';
    [..._countries].sort().forEach(c => {
      const opt = document.createElement('option');
      opt.value = c.name;
      opt.textContent = c.name;
      this._countrySelect.appendChild(opt);
    });
    this._countrySelect.disabled = false;
  }

  _populateCities() {
    const { cities } = this._allFilters;
    const _cities = this._data.region === 'All Regions'? cities : cities.filter((city) => city.country === this._data.country);

    this._citySelect.innerHTML = '<option value="">All Cities</option>';
    [..._cities].sort().forEach(c => {
      const opt = document.createElement('option');
      opt.value = c.name;
      opt.textContent = c.name;
      this._citySelect.appendChild(opt);
    });
    this._citySelect.disabled = false;
  }

  _onRegionChange() {
    // Save map state before first filter change
    if (!this._savedMapState) {
      this._savedMapState = {
        zoom: this._data.zoom,
        centerLat: this._data.centerLat,
        centerLng: this._data.centerLng,
      };
    }

    this._data.region = this._regionSelect.value;
    this._data.country = '';
    this._data.city = '';

    if (this._data.region) {
      this._populateCountries();
      this._showRow(this._countryRow);
    } else {
      this._hideRow(this._countryRow);
      this._countrySelect.innerHTML = '<option value="">All Countries</option>'
    }

    // Always hide city when region changes
    this._hideRow(this._cityRow);
    this._citySelect.innerHTML = '<option value="">All Cities</option>';
    this._citySelect.disabled = true;

    // Show/hide reset button
    this._updateResetButton();
    this._applyFilter();
  }

  _onCountryChange() {
    if (!this._savedMapState) {
      this._savedMapState = {
        zoom: this._data.zoom,
        centerLat: this._data.centerLat,
        centerLng: this._data.centerLng,
      };
    }

    this._data.country = this._countrySelect.value;
    this._data.city = '';

    if (this._data.country) {
      this._populateCities();
      this._showRow(this._cityRow);
    } else {
      this._hideRow(this._cityRow);
    }

    this._citySelect.innerHTML = '<option value="">All Cities</option>';
    if (this._data.country) this._populateCities();

    this._updateResetButton();
    this._applyFilter();
  }

  _onCityChange() {
    if (!this._savedMapState) {
      this._savedMapState = {
        zoom: this._data.zoom,
        centerLat: this._data.centerLat,
        centerLng: this._data.centerLng,
      };
    }

    this._data.city = this._citySelect.value;
    this._updateResetButton();
    this._applyFilter();
  }

  _onBrandChange(){
    if (!this._savedMapState) {
      this._savedMapState = {
        zoom: this._data.zoom,
        centerLat: this._data.centerLat,
        centerLng: this._data.centerLng,
      };
    }
    const _brand = this._brandSelect.options[this._brandSelect.selectedIndex];
    if(_brand.textContent === 'Any'){
      this._data.brand = '';
    } else {
     this._data.brand = {value: _brand.value, textContent: _brand.textContent }
    }
    
    this._applyFilter();
  }

  _onServiceChange(){
    if (!this._savedMapState) {
      this._savedMapState = {
        zoom: this._data.zoom,
        centerLat: this._data.centerLat,
        centerLng: this._data.centerLng,
      };
    }

    const _service = this._serviceSelect.options[this._serviceSelect.selectedIndex];
    if(_service.textContent === 'Any'){
      this._data.service = '';
    } else {
     this._data.service = {value: _service.value, textContent: _service.textContent }
    }
    
    this._applyFilter();
  }

  _onIndustryChange(){
    if (!this._savedMapState) {
      this._savedMapState = {
        zoom: this._data.zoom,
        centerLat: this._data.centerLat,
        centerLng: this._data.centerLng,
      };
    }

    const _industry = this._industrySelect.options[this._industrySelect.selectedIndex];
    if(_industry.textContent === 'Any'){
      this._data.industry = '';
    } else {
     this._data.industry = {value: _industry.value, textContent: _industry.textContent }
    }
    
    this._applyFilter();
  }

  _onStarChange() {
    if (!this._savedMapState) {
      this._savedMapState = {
        zoom: this._data.zoom,
        centerLat: this._data.centerLat,
        centerLng: this._data.centerLng,
      };
    }

    this._data.star = [...this._starMenu.querySelectorAll('input[type="checkbox"]:checked')]
      .map((cb) => cb.value);
    this._updateStarToggleLabel();
    this._applyFilter();
  }

  _onHotelNameChange(_value){
    if (!this._savedMapState) {
      this._savedMapState = {
        zoom: this._data.zoom,
        centerLat: this._data.centerLat,
        centerLng: this._data.centerLng,
      };
    }

    this._data.hotelName = _value
    this._applyFilter();
  }

  _onHotelCodeChange(_value){
    if (!this._savedMapState) {
      this._savedMapState = {
        zoom: this._data.zoom,
        centerLat: this._data.centerLat,
        centerLng: this._data.centerLng,
      };
    }

    this._data.hotelCode = _value
    this._applyFilter();
  }

  _onGuestRoomChange(_value){
    if (!this._savedMapState) {
      this._savedMapState = {
        zoom: this._data.zoom,
        centerLat: this._data.centerLat,
        centerLng: this._data.centerLng,
      };
    }

    this._data.guestRoom = _value
    this._applyFilter();
  }

  _onMeetingRoomChange(_value){
    if (!this._savedMapState) {
      this._savedMapState = {
        zoom: this._data.zoom,
        centerLat: this._data.centerLat,
        centerLng: this._data.centerLng,
      };
    }

    this._data.meetingRoom = _value
    this._applyFilter();
  }

  _onCapacityChange(_value){
    if (!this._savedMapState) {
      this._savedMapState = {
        zoom: this._data.zoom,
        centerLat: this._data.centerLat,
        centerLng: this._data.centerLng,
      };
    }

    this._data.capacity = _value
    this._applyFilter();
  }

  _applyFilter(autoFit = true) {
      const payload = {
        region: this._data.region || '',
        country: this._data.country || '',
        city: this._data.city,
        brand: this._brandSelect.value || '',
        service: this._serviceSelect.value || '',
        industry: this._industrySelect.value || '',
        star: this._data.star,
        guestRoom: this._guestRoomInput.value || '',
        meetingRoom: this._meetingRoomInput.value || '',
        capacity: this._capacityInput.value || '',
        popup: this._hotelPopupTypeSelect.value || '',
        // Only reframe the map for user-initiated filter changes; restoring a
        // saved filter on load must not overwrite the saved zoom/center.
        autoFit,
      }
      this._postToIframe('dynamic-map-filter', payload);

      if (this.block?.dispatchChange) {
        this.block.dispatchChange();
      }
    }

  _autoCenterOnHotels(hotels) {
    const validHotels = hotels.filter(h => h.latitude && h.longitude);
    if (validHotels.length === 0) return;

    let minLat = Infinity, maxLat = -Infinity;
    let minLng = Infinity, maxLng = -Infinity;

    validHotels.forEach(h => {
      const lat = parseFloat(h.latitude);
      const lng = parseFloat(h.longitude);
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
    });

    this._postToIframe('dynamic-map-set-view', {
      bounds: { minLat, maxLat, minLng, maxLng },
    });
  }

  _onReset() {
    // Reset hotel filter
    this._data.region = '';
    this._data.country = '';
    this._data.city = '';
    this._data.brand = '';
    this._data.service = '';
    this._data.industry = '';
    this._data.star = [];
    this._data.guestRoom = 0;
    this._data.meetingRoom = 0;
    this._data.capacity = 0;
    this._data.popup = 'leisure';

    this._regionSelect.value = '';
    this._countrySelect.innerHTML = '<option value="">All Countries</option>';
    this._countrySelect.disabled = true;
    this._citySelect.innerHTML = '<option value="">All Cities</option>';
    this._citySelect.disabled = true;
    this._brandSelect.value = '';
    this._serviceSelect.value = '';

    this._hideRow(this._countryRow);
    this._hideRow(this._cityRow);
    this._syncStarCheckboxes();
    this._closeStarMenu();

    // Restore map position
    if (this._savedMapState) {
      this._postToIframe('dynamic-map-set-view', {
        center: {
          lat: this._savedMapState.centerLat,
          lng: this._savedMapState.centerLng,
        },
        zoom: this._savedMapState.zoom,
      });
    }

    // Show all hotels
    const payload = {
      hotels: this._allHotels,
      region: '',
      country: '',
      city: '',
      brand: '',
      service: '',
    }

    this._postToIframe('dynamic-map-filter', payload);

    this._updateResetButton();
    if (this.block?.dispatchChange) {
      this.block.dispatchChange();
    }
  }

  _updateResetButton() {
    if (this._resetBtn) {
      if (this._data.region || this._data.country || this._data.city) {
        this._resetBtn.style.display = 'inline-block';
      } else {
        this._resetBtn.style.display = 'none';
      }
    }
    if (this._onScreenResetBtn) {
      if (this._data.region || this._data.country || this._data.city) {
        this._onScreenResetBtn.style.display = 'inline-block';
      } else {
        this._onScreenResetBtn.style.display = 'none';
      }
    }
  }

  _showRow(row) {
    if (row) row.querySelector('select').disabled = false;
  }

  _hideRow(row) {
    if (row) row.querySelector('select').disabled = true;
  }


  _setStatus(msg) {
    if (this._status) {
      this._status.textContent = msg;
      this._status.classList.add('visible');
    }
  }

  _hideStatus() {
    if (this._status) {
      this._status.classList.remove('visible');
    }
  }

  _buildModal() {
    const css = this._CSS;
    const modal = document.createElement('div');
    modal.classList.add('dynamic-map-modal');
    
    const backdrop = document.createElement('div');
    backdrop.classList.add(css.editorBlockModalBackdrop);
    backdrop.addEventListener('click', () => this._hideModal());

    const inner = document.createElement('div');
    inner.classList.add('dynamic-map-modal-inner', css.editorBlockModal);

    const heading = document.createElement('h4');
    heading.classList.add(css.editorBlockModalTitle);
    heading.textContent = 'Map settings / hotel filter';

    // Title
    const titleContainer = document.createElement('div');
    titleContainer.classList.add('filter-container');
    const titleLabel = document.createElement('label');
    titleLabel.classList.add(css.editorBlockModalLabel);
    titleLabel.textContent = 'Map Title';
    this._titleInput = document.createElement('input');
    this._titleInput.classList.add(css.editorBlockModalInput);
    this._titleInput.type = 'text';
    this._titleInput.placeholder = 'e.g. Global Portfolio';
    this._titleInput.value = this._data.title;
    titleContainer.appendChild(titleLabel);
    titleContainer.appendChild(this._titleInput);

    // Map style dropdown
    const styleContainer = document.createElement('div');
    styleContainer.classList.add('filter-container');
    const styleLabel = document.createElement('label');
    styleLabel.classList.add(css.editorBlockModalLabel);
    styleLabel.textContent = 'Map Style';
    this._styleSelect = document.createElement('select');
    this._styleSelect.classList.add('dynamic-map-modal-select', css.editorBlockModalInput);
    
    // Map style dropdown
    const hotelPopupTypeContainer = document.createElement('div');
    hotelPopupTypeContainer.classList.add('filter-container');
    const hotelPopupTypeLabel = document.createElement('label');
    hotelPopupTypeLabel.classList.add(css.editorBlockModalLabel);
    hotelPopupTypeLabel.textContent = 'Hotel pop-up type';
    this._hotelPopupTypeSelect = document.createElement('select');
    this._hotelPopupTypeSelect.classList.add('dynamic-map-modal-select', css.editorBlockModalInput);

    const mapAttributeContainer = document.createElement('div');
    mapAttributeContainer.classList.add('filter-group', 'map-attrib');
    mapAttributeContainer.appendChild(titleContainer);
    mapAttributeContainer.appendChild(styleContainer);
    mapAttributeContainer.appendChild(hotelPopupTypeContainer);

    const lightOption = document.createElement('option');
    lightOption.value = 'light';
    lightOption.textContent = 'Light';
    const darkOption = document.createElement('option');
    darkOption.value = 'dark';
    darkOption.textContent = 'Dark';

    this._styleSelect.appendChild(lightOption);
    this._styleSelect.appendChild(darkOption);
    this._styleSelect.value = this._data.mapStyle;
    styleContainer.appendChild(styleLabel);
    styleContainer.appendChild(this._styleSelect);
    
    
    const leisureOption = document.createElement('option');
    leisureOption.value = 'leisure';
    leisureOption.textContent = 'Leisure';
    const meetingOption = document.createElement('option');
    meetingOption.value = 'meeting';
    meetingOption.textContent = 'Meeting';

    this._hotelPopupTypeSelect.appendChild(leisureOption);
    this._hotelPopupTypeSelect.appendChild(meetingOption);
    this._hotelPopupTypeSelect.value = this._data.popup;
    hotelPopupTypeContainer.appendChild(hotelPopupTypeLabel);
    hotelPopupTypeContainer.appendChild(this._hotelPopupTypeSelect);

    // Region filter
    const regionFilterContainer = document.createElement('div');
    regionFilterContainer.classList.add('filter-container');
    const regionLabel = document.createElement('label');
    regionLabel.classList.add(css.editorBlockModalLabel);
    regionLabel.textContent = 'Region';
    this._regionSelect = document.createElement('select');
    this._regionSelect.classList.add('dynamic-map-modal-select', css.editorBlockModalInput);
    this._regionSelect.innerHTML = '<option value="">All Regions</option>';
    this._regionSelect.addEventListener('change', () => this._onRegionChange());
    regionFilterContainer.appendChild(regionLabel);
    regionFilterContainer.appendChild(this._regionSelect);

    // Country filter (row hidden by default)
    this._countryRow = document.createElement('div');
    this._countryRow.classList.add('filter-container');
    const countryLabel = document.createElement('label');
    countryLabel.classList.add(css.editorBlockModalLabel);
    countryLabel.textContent = 'Country';
    this._countrySelect = document.createElement('select');
    this._countrySelect.classList.add('dynamic-map-modal-select', css.editorBlockModalInput);
    this._countrySelect.innerHTML = '<option value="">All Countries</option>';
    this._countrySelect.disabled = true;
    this._countrySelect.addEventListener('change', () => this._onCountryChange());
    this._countryRow.appendChild(countryLabel);
    this._countryRow.appendChild(this._countrySelect);

    // City filter (row hidden by default)
    this._cityRow = document.createElement('div');
    this._cityRow.classList.add('filter-container');
    const cityLabel = document.createElement('label');
    cityLabel.classList.add(css.editorBlockModalLabel);
    cityLabel.textContent = 'City';
    this._citySelect = document.createElement('select');
    this._citySelect.classList.add('dynamic-map-modal-select', css.editorBlockModalInput);
    this._citySelect.innerHTML = '<option value="">All Cities</option>';
    this._citySelect.disabled = true;
    this._citySelect.addEventListener('change', () => this._onCityChange());
    this._cityRow.appendChild(cityLabel);
    this._cityRow.appendChild(this._citySelect);

    // Location filter group (region + country + city)
    const locationFilterContainer = document.createElement('div');
    locationFilterContainer.classList.add('filter-group', 'location-attrib');
    locationFilterContainer.appendChild(regionFilterContainer);
    locationFilterContainer.appendChild(this._countryRow);
    locationFilterContainer.appendChild(this._cityRow);

    // Brand filter 
    const brandFilterContainer = document.createElement('div');
    brandFilterContainer.classList.add('filter-container');
    const brandLabel = document.createElement('label');
    brandLabel.classList.add(css.editorBlockModalLabel);
    brandLabel.textContent = 'Brand';
    this._brandSelect = document.createElement('select');
    this._brandSelect.classList.add('dynamic-map-modal-select', css.editorBlockModalInput);
    this._brandSelect.innerHTML = '<option value="">Any</option>';
    brandFilterContainer.appendChild(brandLabel);
    brandFilterContainer.appendChild(this._brandSelect);
    this._brandSelect.addEventListener('change', () => this._onBrandChange());

    // Services filter 
    const serviceFilterContainer = document.createElement('div');
    serviceFilterContainer.classList.add('filter-container');
    const serviceLabel = document.createElement('label');
    serviceLabel.classList.add(css.editorBlockModalLabel);
    serviceLabel.textContent = 'Features';
    this._serviceSelect = document.createElement('select');
    this._serviceSelect.classList.add('dynamic-map-modal-select', css.editorBlockModalInput);
    this._serviceSelect.innerHTML = '<option value="">Any</option>';
    serviceFilterContainer.appendChild(serviceLabel);
    serviceFilterContainer.appendChild(this._serviceSelect);
    this._serviceSelect.addEventListener('change', () => this._onServiceChange());
    
    // Industries filter 
    const industryFilterContainer = document.createElement('div');
    industryFilterContainer.classList.add('filter-container');
    const industryLabel = document.createElement('label');
    industryLabel.classList.add(css.editorBlockModalLabel);
    industryLabel.textContent = 'Industries';
    this._industrySelect = document.createElement('select');
    this._industrySelect.classList.add('dynamic-map-modal-select', css.editorBlockModalInput);
    this._industrySelect.innerHTML = '<option value="">Any</option>';
    industryFilterContainer.appendChild(industryLabel);
    industryFilterContainer.appendChild(this._industrySelect);
    this._industrySelect.addEventListener('change', () => this._onIndustryChange());
    
    // Stars filter 
    const starFilterContainer = document.createElement('div');
    starFilterContainer.classList.add('filter-container');
    const starLabel = document.createElement('label');
    starLabel.classList.add(css.editorBlockModalLabel);
    starLabel.textContent = 'Stars';
    starFilterContainer.appendChild(starLabel);
    starFilterContainer.appendChild(this._buildStarDropdown());

    const debounce = (fn, delay = 300) => {
      let timeout;

      return (...args) => {
          clearTimeout(timeout);
          timeout = setTimeout(() => fn(...args), delay);
      };
    };

    const hotelNameContainer = document.createElement('div');
    hotelNameContainer.classList.add('filter-container');
    const hotelNameLabel = document.createElement('label');
    hotelNameLabel.classList.add(css.editorBlockModalLabel);
    hotelNameLabel.textContent = 'Hotel name';
    this._hotelNameInput = document.createElement('input');
    this._hotelNameInput.classList.add('dynamic-map-modal-input', css.editorBlockModalInput);
    hotelNameContainer.appendChild(hotelNameLabel);
    hotelNameContainer.appendChild(this._hotelNameInput);

    this._hotelNameInput.addEventListener(
      'input',
      debounce((event) => {
          const value = event.target.value;
          this._onHotelNameChange(value);
      }, 300)
    );

    const hotelCodeContainer = document.createElement('div');
    hotelCodeContainer.classList.add('filter-container');
    const hotelCodeLabel = document.createElement('label');
    hotelCodeLabel.classList.add(css.editorBlockModalLabel);
    hotelCodeLabel.textContent = 'Hotel EMMA code';
    this._hotelCodeInput = document.createElement('input');
    this._hotelCodeInput.classList.add('dynamic-map-modal-input', css.editorBlockModalInput);
    hotelCodeContainer.appendChild(hotelCodeLabel);
    hotelCodeContainer.appendChild(this._hotelCodeInput);

    this._hotelCodeInput.addEventListener(
      'input',
      debounce((event) => {
          const value = event.target.value;
          this._onHotelCodeChange(value);
      }, 300)
    );

    const hotelAttribContainer = document.createElement('div');
    hotelAttribContainer.classList.add('filter-group','hotel-attrib');
    hotelAttribContainer.appendChild(brandFilterContainer);
    hotelAttribContainer.appendChild(industryFilterContainer);
    hotelAttribContainer.appendChild(serviceFilterContainer);
    hotelAttribContainer.appendChild(starFilterContainer);
    
    // Guest rooms filter 
    const guestRoomFilterContainer = document.createElement('div');
    guestRoomFilterContainer.classList.add('filter-container');
    const guestRoomLabel = document.createElement('label');
    guestRoomLabel.classList.add(css.editorBlockModalLabel);
    guestRoomLabel.textContent = 'Min. guest rooms';
    this._guestRoomInput = document.createElement('input');
    this._guestRoomInput.classList.add('dynamic-map-modal-input', css.editorBlockModalInput);
    this._guestRoomInput.type = "number";
    this._guestRoomInput.min = 0;
    this._guestRoomInput.value = this._data.guestRoom;
    guestRoomFilterContainer.appendChild(guestRoomLabel);
    guestRoomFilterContainer.appendChild(this._guestRoomInput);

    this._guestRoomInput.addEventListener(
      'input',
      debounce((event) => {
          const value = event.target.value;
          this._onGuestRoomChange(parseInt(value));
      }, 300)
    );

    // Meeting rooms filter 
    const meetingRoomFilterContainer = document.createElement('div');
    meetingRoomFilterContainer.classList.add('filter-container');
    const meetingRoomLabel = document.createElement('label');
    meetingRoomLabel.classList.add(css.editorBlockModalLabel);
    meetingRoomLabel.textContent = 'Min. meeting rooms';
    this._meetingRoomInput = document.createElement('input');
    this._meetingRoomInput.classList.add('dynamic-map-modal-input', css.editorBlockModalInput);
    this._meetingRoomInput.type = "number";
    this._meetingRoomInput.min = 0;
    this._meetingRoomInput.value = this._data.meetingRoom;
    meetingRoomFilterContainer.appendChild(meetingRoomLabel);
    meetingRoomFilterContainer.appendChild(this._meetingRoomInput);

    this._meetingRoomInput.addEventListener(
      'input',
      debounce((event) => {
          const value = event.target.value;
          this._onMeetingRoomChange(parseInt(value));
      }, 300)
    );

    // Capacity filter 
    const capacityFilterContainer = document.createElement('div');
    capacityFilterContainer.classList.add('filter-container');
    const capacityLabel = document.createElement('label');
    capacityLabel.classList.add(css.editorBlockModalLabel);
    capacityLabel.textContent = 'Min. capacity';
    this._capacityInput = document.createElement('input');
    this._capacityInput.classList.add('dynamic-map-modal-input', css.editorBlockModalInput);
    this._capacityInput.type = "number";
    this._capacityInput.min = 0;
    this._capacityInput.value = this._data.capacity;
    capacityFilterContainer.appendChild(capacityLabel);
    capacityFilterContainer.appendChild(this._capacityInput);

    this._capacityInput.addEventListener(
      'input',
      debounce((event) => {
          const value = event.target.value;
          this._onCapacityChange(parseInt(value));
      }, 300)
    );

    const hotelNumsContainer = document.createElement('div');
    hotelNumsContainer.classList.add('filter-group','hotel-nums');
    hotelNumsContainer.appendChild(guestRoomFilterContainer);
    hotelNumsContainer.appendChild(meetingRoomFilterContainer);
    hotelNumsContainer.appendChild(capacityFilterContainer);

    // Reset button
    const resetBtnContainer = document.createElement("div");
    resetBtnContainer.classList.add(css.dmModalResetContainer);
    this._resetBtn = document.createElement('button');
    this._resetBtn.classList.add(css.dmModalResetBtn, css.blockBtn, css.blockBtnLarge, css.blockBtnLargeWide, css.clear);
    this._resetBtn.textContent = 'Reset';
    this._resetBtn.style.display = 'none';
    this._resetBtn.addEventListener('click', () => {
      this._onReset();
    });

    // Actions
    const actions = document.createElement('div');
    actions.classList.add('dynamic-map-modal-actions');

    const cancelBtn = document.createElement('button');
    cancelBtn.classList.add(css.dmModalCancelBtn, css.blockBtn, css.blockBtnLarge, css.blockBtnLargeWide, css.cancel);
    cancelBtn.textContent = 'Cancel';
    cancelBtn.addEventListener('click', () => this._hideModal());

    const applyBtn = document.createElement('button');
    applyBtn.classList.add(css.dmModalApplyBtn, css.blockBtn,  css.blockBtnLarge, css.blockBtnLargeWide, css.save);
    applyBtn.textContent = 'Apply';
    applyBtn.addEventListener('click', () => this._applySettings());

    resetBtnContainer.appendChild(this._resetBtn);
    actions.appendChild(resetBtnContainer);
    actions.appendChild(cancelBtn);
    actions.appendChild(applyBtn);

    const mapSettingSection = document.createElement('div');
    mapSettingSection.classList.add('map-setting-section');

    mapAttributeContainer.appendChild(this._buildOfflineFallbackSection());

    inner.appendChild(actions);
    inner.appendChild(heading);
    mapSettingSection.appendChild(mapAttributeContainer);
    mapSettingSection.appendChild(locationFilterContainer);
    mapSettingSection.appendChild(hotelAttribContainer);
    mapSettingSection.appendChild(hotelNumsContainer);
    inner.appendChild(mapSettingSection);

    modal.appendChild(backdrop);
    modal.appendChild(inner);

    return modal;
  }

  _buildStarDropdown() {
    const css = this._CSS;
    this._starDropdown = document.createElement('div');
    this._starDropdown.classList.add('dm-checkbox-dropdown');

    this._starToggle = document.createElement('button');
    this._starToggle.type = 'button';
    this._starToggle.classList.add('dm-checkbox-dropdown-toggle', css.editorBlockModalInput);
    this._starToggle.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this._starMenu.classList.contains('open') ? this._closeStarMenu() : this._openStarMenu();
    });

    this._starMenu = document.createElement('div');
    this._starMenu.classList.add('dm-checkbox-dropdown-menu');

    this._starDropdown.appendChild(this._starToggle);
    this._starDropdown.appendChild(this._starMenu);
    this._updateStarToggleLabel();
    return this._starDropdown;
  }

  _openStarMenu() {
    this._starMenu.classList.add('open');
    document.addEventListener('click', this._onDocClick);
  }

  _closeStarMenu() {
    this._starMenu?.classList.remove('open');
    document.removeEventListener('click', this._onDocClick);
  }

  _renderStarOptions(min, max) {
    this._starMenu.innerHTML = '';
    for (let i = Number(min); i <= Number(max); i++) {
      const label = document.createElement('label');
      label.classList.add('dm-checkbox-option');

      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.value = String(i);
      cb.checked = this._data.star.includes(String(i));
      cb.addEventListener('change', () => this._onStarChange());

      const text = document.createElement('span');
      text.textContent = String(i);

      label.appendChild(cb);
      label.appendChild(text);
      this._starMenu.appendChild(label);
    }
    this._updateStarToggleLabel();
  }

  _syncStarCheckboxes() {
    if (!this._starMenu) return;
    this._starMenu.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
      cb.checked = this._data.star.includes(cb.value);
    });
    this._updateStarToggleLabel();
  }

  _updateStarToggleLabel() {
    if (!this._starToggle) return;
    const s = this._data.star;
    this._starToggle.textContent = s.length
      ? [...s].sort((a, b) => a - b).join(', ')
      : 'Any';
  }

  _buildOfflineFallbackSection() {
    const css = this._CSS;
    const wrapper = document.createElement('div');
    wrapper.classList.add('dm-offline-fallback');

    const label = document.createElement('label');
    label.classList.add(css.editorBlockModalLabel);
    label.textContent = 'Offline fallback image';

    this._offlineFallbackContainer = document.createElement('div');
    this._offlineFallbackContainer.classList.add(
      'modal-fallback-img-container',
      'editor-block-modal-img-container',
    );

    this._renderOfflineFallback();

    wrapper.appendChild(label);
    wrapper.appendChild(this._offlineFallbackContainer);
    return wrapper;
  }

  _renderOfflineFallback() {
    const css = this._CSS;
    if (!this._offlineFallbackContainer) return;

    this._offlineFallbackContainer.innerHTML = '';

    if (this._data.url) {
      const img = document.createElement('img');
      img.src = this._data.url;
      img.setAttribute('data-mid', this._data.mid);
      img.setAttribute('data-x', this._data.x);
      img.setAttribute('data-y', this._data.y);
      img.setAttribute('data-width', this._data.width);
      img.setAttribute('data-height', this._data.height);
      img.setAttribute('data-maxwidth', this._data.maxWidth);
      img.setAttribute('data-maxheight', this._data.maxHeight);
      img.classList.add('modal-fallback-img');
      img.style.width = '200px';
      img.style.height = '112.5px';
      img.style.objectFit = 'cover';
      this._offlineFallbackContainer.appendChild(img);
    }

    const btn = document.createElement('button');
    btn.classList.add(
      'map-fallback-btn',
      css.blockBtn,
      css.blockBtnSmall,
      css.xxlIcon,
    );
    btn.innerHTML = ImageIcon;
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      if (!this.config?.showMediaLibrary) return;
      const blockIndex = this.api.blocks.getCurrentBlockIndex();
      const block = this.api.blocks.getBlockByIndex(blockIndex);
      this.config.showMediaLibrary({
        details: block,
        data: this._data,
        bundle: 'image',
        slideFiles: {
          minWidth: 1366,
          minHeight: 768,
          maxWidth: 1920,
          maxHeight: 1080,
        },
      });
    });

    this._offlineFallbackContainer.appendChild(btn);
  }

  _showModal() {
    if (this._modal) {
      this._titleInput.value = this._data.title;
      this._styleSelect.value = this._data.mapStyle;
      this._hotelPopupTypeSelect.value = this._data.popup;
      this._updateResetButton();
      this._modal.classList.add('show');
    }
  }

  _hideModal() {
    if (this._modal) {
      this._modal.classList.remove('show');
    }
    this._closeStarMenu();
  }

  _applySettings() {
    this._data.title = this._titleInput.value.trim();
    this._data.mapStyle = this._styleSelect.value;
    this._data.popup = this._hotelPopupTypeSelect.value;

    this._hideModal();

    // Re-render iframe with new params
    this._renderIframe();

    if (this.block?.dispatchChange) {
      this.block.dispatchChange();
    }
  }

  save() {
    return {
      title: this._data.title,
      zoom: this._data.zoom,
      centerLat: this._data.centerLat,
      centerLng: this._data.centerLng,
      mapStyle: this._data.mapStyle,
      region: this._data.region,
      country: this._data.country,
      city: this._data.city,
      brand: this._data.brand || '',
      service: this._data.service || '',
      industry: this._data.industry || '',
      star: Array.isArray(this._data.star) ? this._data.star : [],
      guestRoom: this._data.guestRoom || 0,
      meetingRoom: this._data.meetingRoom || 0,
      capacity: this._data.capacity || 0,
      popup: this._data.popup || 'leisure',
      url: this._data.url || '',
      mid: this._data.mid || '',
      x: this._data.x || '',
      y: this._data.y || '',
      width: this._data.width || '',
      height: this._data.height || '',
      maxWidth: this._data.maxWidth || '',
      maxHeight: this._data.maxHeight || '',
    };
  }

  validate() {
    return true;
  }

  destroyed() {
    window.removeEventListener('message', this._onMessage);
    document.removeEventListener('click', this._onDocClick);
  }
}
